import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "frame", "loading", "error", "title", "copy"]

  static values = {
    sessionUrl: String,
    title: String,
    copy: String
  }

  connect() {
    this.abortController = null
    this.boundEscape = this.handleEscape.bind(this)
    this.reset()
  }

  disconnect() {
    this.abortPending()
    this.teardownFrame()
    document.removeEventListener("keydown", this.boundEscape)
  }

  async open(event) {
    event?.preventDefault()
    if (!this.hasModalTarget || !this.hasFrameTarget) return

    this.abortPending()
    this.reset()
    this.modalTarget.hidden = false
    document.addEventListener("keydown", this.boundEscape)
    this.showLoading("Opening secure Daily lane...")

    this.abortController = new AbortController()

    try {
      const payload = await this.postJson(this.sessionUrlValue)
      const meetingUrl = payload.meeting_url || payload.iframe_url
      if (!meetingUrl) throw new Error("Daily did not return a meeting URL.")

      if (this.hasTitleTarget && payload.title) this.titleTarget.textContent = payload.title
      if (this.hasCopyTarget && payload.copy) this.copyTarget.textContent = payload.copy

      this.frameTarget.src = meetingUrl
      this.frameTarget.hidden = false
    } catch (error) {
      console.error("❌ Daily video failed:", error)
      this.showError(error.message || "Unable to start Daily video right now.")
    } finally {
      this.abortPending()
    }
  }

  close(event) {
    event?.preventDefault()
    this.abortPending()
    this.modalTarget.hidden = true
    document.removeEventListener("keydown", this.boundEscape)
    this.teardownFrame()
    this.reset()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  loaded() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  reset() {
    if (this.hasFrameTarget) this.frameTarget.hidden = true
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
    if (this.hasErrorTarget) {
      this.errorTarget.hidden = true
      this.errorTarget.textContent = ""
    }
    if (this.hasTitleTarget && this.hasTitleValue) this.titleTarget.textContent = this.titleValue
    if (this.hasCopyTarget && this.hasCopyValue) this.copyTarget.textContent = this.copyValue
  }

  teardownFrame() {
    if (this.hasFrameTarget) {
      this.frameTarget.src = "about:blank"
      this.frameTarget.hidden = true
    }
  }

  showLoading(message) {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.hidden = false
    this.loadingTarget.textContent = message
  }

  showError(message) {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
  }

  abortPending() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  async postJson(url) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      credentials: "same-origin",
      signal: this.abortController?.signal
    })

    const payload = await response.json().catch(() => ({}))
    if (!response.ok || payload.success === false) {
      throw new Error(payload.error || payload.message || `HTTP ${response.status}`)
    }

    return payload
  }
}
